/**
 * TermsModal.jsx
 * 서비스 이용약관 모달 컴포넌트 - DevSetupModal 디자인 언어 적용
 */

import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

export default function TermsModal({ isOpen, onClose, onAgree }) {
    const [scrolled, setScrolled] = useState(false);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (onAgree) {
            onAgree();
        } else {
            onClose();
        }
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md"
                onClick={onClose}
            />

            <motion.div
                key="modal-content"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
            >
                <div
                    className="w-full max-w-2xl bg-[#0f1219] border border-white/10 rounded-2xl shadow-2xl flex flex-col pointer-events-auto max-h-[85vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className={`flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0 transition-all duration-300 ${scrolled ? 'bg-[#0f1219] shadow-lg' : 'bg-gradient-to-r from-[#0f1219] to-[#1e293b]'}`}>
                        <div>
                            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                                Terms of Service
                            </h2>
                            <p className="text-slate-400 text-xs mt-0.5">LUNA AI Tutor 서비스 이용약관</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div
                        className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar space-y-6"
                        onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 10)}
                    >
                        <Section number="01" title="서비스 목적 및 비영리 원칙">
                            본 서비스 <strong className="text-cyan-300">'AI Coding Tutor'</strong>는 SW 교육 및 개인 학습 보조를 위해 제작되었습니다.
                            <br />모든 콘텐츠는 <span className="text-white font-medium">비영리 목적</span>으로만 제공되며, 상업적 용도의 재배포를 금지합니다.
                        </Section>

                        <Section number="02" title="지식재산권 귀속">
                            본 서비스의 기획, 소스 코드, 디자인 및 AI 페르소나('루나')의 권리는 <strong className="text-white">LUNA Project</strong>에게 있습니다. 무단 도용 시 법적 책임을 물을 수 있습니다.
                        </Section>

                        <Section number="03" title="AI 생성 콘텐츠 면책">
                            Google Gemini API를 활용합니다. AI 생성 정보의 정확성을 100% 보장하지 않으며, 실무 적용에 따른 책임은 사용자에게 있습니다.
                        </Section>

                        <Section number="04" title="개인정보 수집 및 이용">
                            학습 이력 관리(커리큘럼, XP 등)를 위해 최소한의 정보만 수집하며, 절대 타 용도로 사용하지 않습니다.
                        </Section>

                        {/* Contact */}
                        <div className="pt-4 border-t border-white/5 text-center">
                            <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-1">Contact</span>
                            <span className="text-sm text-slate-400 font-mono">
                                syjang0803@gmail.com
                            </span>
                        </div>
                    </div>

                    {/* Footer Action */}
                    <div className="px-6 py-4 border-t border-white/5 shrink-0 bg-black/20">
                        <button
                            onClick={handleConfirm}
                            className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-sm transition-all shadow-lg shadow-cyan-900/30"
                        >
                            {onAgree ? '위 내용을 확인하였으며 동의합니다' : '확인'}
                            <i className="fas fa-check ml-2"></i>
                        </button>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
}

function Section({ number, title, children }) {
    return (
        <div className="group">
            <h4 className="flex items-center gap-3 text-sm font-bold text-white mb-2">
                <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded">{number}</span>
                {title}
            </h4>
            <div className="text-sm text-slate-400 leading-relaxed pl-10">
                {children}
            </div>
        </div>
    );
}
